import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { User } from "@models/user";
import { useAtom } from "jotai";
import { debounce } from "lodash";
import { useTranslations } from "next-intl";
import Image from "next/image";
import ErrorIcon from "public/images/fluent-error-circle.svg";
import { hasTransactionByEmail } from "services/transaction";
import { languageAtom } from "utils/atoms";
import { transformEmailAddr } from "utils/misc";
import { TicketID } from "utils/mongo/tickets.model";

interface InputProps {
  label: string;
  inputName: string;
  placeholder: string;
  required: boolean;
  onlyForTicketType?: string[];
  span?: number;
}
interface FormControlWrapperProps extends InputProps {
  register: any;
  errors: any;
  attendee: number;
  watch: any;
  userEmailFirstTime: boolean;
  user: User | null;
  trigger: any;
}

const FormControlWrapper = ({
  label,
  inputName,
  placeholder,
  required,
  register,
  errors,
  attendee,
  watch,
  userEmailFirstTime,
  user,
  trigger,
  span,
}: FormControlWrapperProps) => {
  const [isMobile] = useMediaQuery("(max-width: 640px)");
  const t = useTranslations("checkout");
  const inputName_ = `${attendee}-${inputName}`;
  const disabledFirstEmailInput =
    userEmailFirstTime &&
    (inputName_ === "0-emailAddress" || inputName_ === "0-confirmEmailAddress");
  const b = userEmailFirstTime ? inputName_ !== "0-emailAddress" : true;
  const b_ = userEmailFirstTime ? inputName_ !== "0-confirmEmailAddress" : true;

  const checkDuplicate = (v: string, inputName: string) => {
    const allSameInputs = Object.keys(watch()).filter((x) =>
      x.match(new RegExp(`[0-9]+-${inputName}`, "g"))
    );
    const otherSameInputs = allSameInputs.filter((x) => x !== inputName_);
    return !otherSameInputs
      .map((i) => watch()[i])
      .includes(transformEmailAddr(v));
  };
  return (
    <FormControl
      isInvalid={Boolean(errors[inputName_])}
      style={{ gridColumn: isMobile ? "span 1" : `span ${span}` }}
    >
      <FormLabel htmlFor={inputName_} variant="custom">
        <p>
          <span>{label} </span>
          {required && <span className="text-primary-300">*</span>}
        </p>
      </FormLabel>
      <Input
        type={"text"}
        //isDisabled={disabledFirstEmailInput}
        readOnly={disabledFirstEmailInput}
        //value={disabledFirstEmailInput ? user?.email : undefined}
        placeholder={placeholder}
        _placeholder={{ opacity: 0.3 }}
        {...register(inputName_, {
          required: required && `${label} ${t("required")}`,
          pattern: inputName === "emailAddress" && {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: t("invalidEmail"),
          },
          validate:
            (inputName === "emailAddress" &&
              b && {
                noDuplicate: (v: string) =>
                  checkDuplicate(v, inputName) || t("duplicateEmail"),
                checkEmail: async (v: string) =>
                  (await hasTransactionByEmail(v)) || t("emailPurchased"),
              }) ||
            (inputName === "confirmEmailAddress" &&
              b_ && {
                confirmEmail: (v: string) =>
                  v === watch(`${attendee}-emailAddress`) || t("emailMismatch"),
              }),
          onChange: debounce(async () => {
            await trigger(inputName_);
          }, 1000),
        })}
      />
      <FormErrorMessage>{errors[inputName_]?.message}</FormErrorMessage>
    </FormControl>
  );
};

const AccordionItemWrapper = ({
  buttonTitle,
  children,
  isLast,
  attendee,
  errors,
}: {
  buttonTitle: string;
  children: React.ReactNode;
  isLast: boolean;
  attendee: number;
  errors: any;
}) => {
  const hasError =
    Object.keys(errors).filter((x) =>
      x.match(new RegExp(`${attendee}+-.*`, "g"))
    ).length > 0;
  return (
    <AccordionItem borderTop={0} _last={{ borderBottom: "0" }}>
      <h2>
        <AccordionButton
          _hover={
            {
              //bg: "neutral.900",
            }
          }
          bg="neutral.900"
          px="1.25rem"
          borderTopRadius={!!attendee ? "" : "0.5rem"}
          borderWidth="2px"
          borderColor={hasError ? "red.500" : "#DEDEE0"}
        >
          <Box
            fontWeight={500}
            fontSize="lg"
            color="neutral.300"
            flex="1"
            textAlign="left"
          >
            <Text fontSize="2xl" fontWeight={500} my={"0.5rem"}>
              {buttonTitle}
            </Text>
          </Box>
          {hasError && (
            <div className="w-10 mr-5">
              <Image layout="responsive" src={ErrorIcon} alt="error icon" />
            </div>
          )}
          <AccordionIcon width="2rem" fontSize="2rem" color="neutral.300" />
        </AccordionButton>
      </h2>
      <AccordionPanel
        borderWidth={"2px"}
        borderColor={hasError ? "red.500" : "#DEDEE0"}
        borderTop={0}
        px="1.25rem"
        py="2.5rem"
        borderBottomRadius={isLast ? "0.5rem" : ""}
      >
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
};

const ContactInformation = ({
  attendee,
  register,
  errors,
  selectedTicketType,
  //validateEmail,
  watch,
  isLast,
  userEmailFirstTime,
  user,
  trigger,
}: {
  attendee: number;
  register: any;
  errors: any;
  selectedTicketType: TicketID;
  //validateEmail: (email: string) => Promise<boolean>;
  watch: any;
  isLast: boolean;
  userEmailFirstTime: boolean;
  user: User | null;
  trigger: any;
}) => {
  const [lang] = useAtom(languageAtom);
  const t = useTranslations("checkout");
  const inputList: InputProps[] = [
    {
      label: t("emailAddress"),
      inputName: "emailAddress",
      placeholder: "john@gmail.com",
      required: true,
    },
    {
      label: t("confirmEmailAddress"),
      inputName: "confirmEmailAddress",
      placeholder: "john@gmail.com",
      required: true,
    },
    {
      label: "Name",
      inputName: "name",
      placeholder: "",
      required: true,
      span: 2,
    },
    //{
    //  label: t("phoneNumber"),
    //  inputName: "phoneNumber",
    //  placeholder: "",
    //  required: false,
    //  span: 2,
    //},
    //{
    //  label: "Core Wallet Address",
    //  inputName: "coreWalletAddress",
    //  placeholder: "",
    //  required: false,
    //  span: 2,
    //},
  ];
  return (
    <>
      <AccordionItemWrapper
        //buttonTitle={`${t("ticket")} ${attendee + 1}`}
        buttonTitle={`Member Details`}
        isLast={isLast}
        attendee={attendee}
        errors={errors}
      >
        <div className="grid  grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
          {inputList.map(
            (input, index) =>
              (!input.onlyForTicketType ||
                input.onlyForTicketType?.includes(selectedTicketType)) && (
                <FormControlWrapper
                  key={index}
                  {...input}
                  register={register}
                  errors={errors}
                  attendee={attendee}
                  //validateEmail={validateEmail}
                  watch={watch}
                  userEmailFirstTime={userEmailFirstTime}
                  user={user}
                  trigger={trigger}
                />
              )
          )}
        </div>
      </AccordionItemWrapper>
    </>
  );
};

export default ContactInformation;
